/**
 * Extracts the exact error message returned by backend APIs.
 * Prioritizes raw string responses, followed by common JSON error fields.
 * Ensures no hardcoded UI message overrides a message provided by the API.
 */
export const getApiFailureMessage = (error: any, method?: string): string => {
    const updatedMethod: Record<string, string> = {
        Create: 'creating',
        Update: 'updating',
        Delete: 'deleting',
        Clone: 'cloning',
        Fetch: 'fetching',
        Generate: 'generating',
        Save: 'saving',
        Post: 'posting',
    };

    const baseMessage = method
        ? `Something went wrong while ${updatedMethod[method] || method}`
        : 'Something went wrong. Try again.';

    // Priority 1: Raw plain text or string response from API (e.g. backend 400 string body)
    if (typeof error?.response?.data === 'string' && error.response.data.trim()) {
        return error.response.data;
    }

    // Helper to extract a readable string from an individual error item
    const formatErrorItem = (item: any): string => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
            return item.msg || item.message || item.error || JSON.stringify(item);
        }
        return String(item);
    };

    // Priority 2: Extract message from response object or array (supporting any backend key format)
    const resData = error?.response?.data;
    if (Array.isArray(resData) && resData.length > 0) {
        const formatted = resData.map(formatErrorItem).filter(Boolean).join('; ');
        if (formatted) return formatted;
    }

    if (resData && typeof resData === 'object') {
        if (Array.isArray(resData.detail) && resData.detail.length > 0) {
            const formatted = resData.detail.map(formatErrorItem).filter(Boolean).join('; ');
            if (formatted) return formatted;
        }
        if (Array.isArray(resData.errors) && resData.errors.length > 0) {
            const formatted = resData.errors.map(formatErrorItem).filter(Boolean).join('; ');
            if (formatted) return formatted;
        }
        if (Array.isArray(resData.messages) && resData.messages.length > 0) {
            const formatted = resData.messages.map(formatErrorItem).filter(Boolean).join('; ');
            if (formatted) return formatted;
        }

        const apiMsg =
            resData.message ||
            resData.Message ||
            resData.error ||
            resData.Error ||
            resData.detail ||
            resData.Detail ||
            resData.errorMessage ||
            resData.msg ||
            resData.description ||
            resData.data;

        if (typeof apiMsg === 'string' && apiMsg.trim()) {
            return apiMsg;
        }
    }

    // Priority 3: Axios error message if present and specific (not generic network error code)
    if (
        typeof error?.message === 'string' &&
        error.message.trim() &&
        !error.message.startsWith('Request failed with status code')
    ) {
        return error.message;
    }

    return baseMessage;
};
